/**
 * Phase 4.5 — Editing Guard Registry
 *
 * Forms register themselves on mount and unregister on unmount.
 * The orchestrator queries `getDirtyForms()` whenever it wants to switch
 * context due to an incoming call; if the list is non-empty, the
 * SwitchContextDialog is shown to the user with the form labels.
 *
 * Each registration must provide:
 *   id      — stable string (unique per page)
 *   isDirty — () => boolean
 *   save    — () => Promise<void>  (called when user clicks "Save & Switch")
 *   discard — () => Promise<void>  (called when user clicks "Discard & Switch")
 *   label   — human-readable form name (i18n key resolved by caller)
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useEditingGuardRegistry = defineStore('editingGuardRegistry', () => {
  const forms = ref(new Map())

  function register(entry) {
    if (!entry || !entry.id) return
    forms.value.set(entry.id, entry)
  }

  function unregister(id) {
    if (!id) return
    forms.value.delete(id)
  }

  function getDirtyForms() {
    const out = []
    forms.value.forEach((entry) => {
      try {
        if (entry.isDirty && entry.isDirty()) {
          out.push(entry)
        }
      } catch (e) {
        console.warn('[editingGuard] isDirty() threw for form', entry.id, e)
      }
    })
    return out
  }

  async function saveAll() {
    const tasks = []
    forms.value.forEach((entry) => {
      try {
        if (entry.isDirty && entry.isDirty() && typeof entry.save === 'function') {
          tasks.push(Promise.resolve(entry.save()))
        }
      } catch (e) {
        console.warn('[editingGuard] save() threw for form', entry.id, e)
      }
    })
    await Promise.all(tasks)
  }

  async function discardAll() {
    const tasks = []
    forms.value.forEach((entry) => {
      try {
        if (entry.isDirty && entry.isDirty() && typeof entry.discard === 'function') {
          tasks.push(Promise.resolve(entry.discard()))
        }
      } catch (e) {
        console.warn('[editingGuard] discard() threw for form', entry.id, e)
      }
    })
    await Promise.all(tasks)
  }

  return { forms, register, unregister, getDirtyForms, saveAll, discardAll }
})
